import React, { useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, PillButton, Toggle } from '../ui';
import { parseDotenv } from '../utils/dotenv';
import { rowProblems, rowsFromImport, mergeImported, fromRows, toRows } from '../utils/environment';
import { ReactComponent as PlusIcon } from '../assets/icons/plus.svg';
import { ReactComponent as TrashIcon } from '../assets/icons/trash.svg';
import { ReactComponent as CopyIcon } from '../assets/icons/copy.svg';
import { ReactComponent as EyeIcon } from '../assets/icons/eye.svg';
import { ReactComponent as EyeOffIcon } from '../assets/icons/eye-off.svg';
import { ReactComponent as UploadIcon } from '../assets/icons/upload.svg';
import { ReactComponent as LinkIcon } from '../assets/icons/link.svg';
import { ReactComponent as InfoIcon } from '../assets/icons/info.svg';
import './EnvironmentEditor.css';

let rowCounter = 0;
const newRow = (over = {}) => ({ id: `n${rowCounter++}`, key: '', mode: 'value', value: '', ref: '', ...over });

/**
 * Editor de las variables de un entorno (tabla clave / valor o referencia). Es controlado: `rows` y `base` viven en el
 * formulario; `onChange({ rows, base })` en cada cambio. Los valores se ven ocultos hasta que se pulsa el ojo.
 *
 * Importar un .env: pegado o subido. Un navegador no puede borrar el fichero que se subio: la opcion "borrar el fichero
 * despues" es un recordatorio que sale al terminar de importar. Exportar esta en la vista del item (EnvironmentView).
 */
function EnvironmentEditor({ rows, base, selfName, otherEnvironments, onChange }) {
  const { t } = useTranslation();
  const [revealed, setRevealed] = useState({});
  const [importOpen, setImportOpen] = useState(false);
  const [pasted, setPasted] = useState('');
  const [fileName, setFileName] = useState('');
  const [replace, setReplace] = useState(false);
  const [remind, setRemind] = useState(true);
  const [importSummary, setImportSummary] = useState(null); // { added, replaced, skipped, errors, file }
  const [reminder, setReminder] = useState(null); // nombre del fichero a borrar
  const fileInput = useRef(null);

  const problems = useMemo(() => rowProblems(rows), [rows]);

  const update = (next) => onChange({ rows: next.rows ?? rows, base: next.base ?? base });
  const setRow = (id, patch) => update({ rows: rows.map((r) => (r.id === id ? { ...r, ...patch } : r)) });
  const removeRow = (id) => update({ rows: rows.filter((r) => r.id !== id) });
  const copyKey = (key) => { if (key) navigator.clipboard?.writeText(key).catch(() => {}); };

  const preview = useMemo(() => (pasted.trim() ? parseDotenv(pasted) : { entries: [], errors: [] }), [pasted]);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) { setImportSummary({ tooLarge: true }); e.target.value = ''; return; }
    const reader = new FileReader();
    reader.onload = () => { setPasted(String(reader.result || '')); setFileName(file.name); };
    reader.readAsText(file);
    e.target.value = '';
  };

  const closeImport = () => { setImportOpen(false); setPasted(''); setFileName(''); };

  const doImport = () => {
    const current = fromRows(rows);
    const merged = mergeImported(current, rowsFromImport(preview.entries), { replace });
    update({ rows: toRows(merged.variables).map((r) => ({ ...r, id: `i${rowCounter++}` })) });
    setImportSummary({ added: merged.added, replaced: merged.replaced, skipped: merged.skipped, errors: preview.errors });
    setReminder(remind && fileName ? fileName : null);
    closeImport();
  };

  return (
    <div className="sgc-env-editor">
      <div className="sgc-env-editor__toolbar">
        <h2 className="sgc-env-editor__title">{t('environment.variables')}</h2>
        <PillButton variant="secondary" size="sm" icon={UploadIcon} onClick={() => setImportOpen(true)}>{t('environment.import')}</PillButton>
      </div>

      {reminder && (
        <div className="sgc-env-editor__notice sgc-env-editor__notice--warn" role="status">
          <InfoIcon width={18} height={18} aria-hidden="true" />
          <div>
            <strong>{t('environment.deleteFileTitle')}</strong>
            <p>{t('environment.deleteFileBody', { name: reminder })}</p>
          </div>
          <button type="button" onClick={() => setReminder(null)}>{t('environment.dismiss')}</button>
        </div>
      )}
      {importSummary && !importSummary.tooLarge && (
        <p className="sgc-env-editor__summary" role="status">
          {t('environment.importResult', { added: importSummary.added, replaced: importSummary.replaced, skipped: importSummary.skipped })}
          {importSummary.errors.length > 0 && ` ${t('environment.importLinesSkipped', { lines: importSummary.errors.map((e) => e.line).join(', ') })}`}
        </p>
      )}
      {importSummary?.tooLarge && <p className="sgc-env-editor__summary sgc-env-editor__summary--error" role="alert">{t('environment.importTooLarge')}</p>}

      {rows.length === 0 ? (
        <p className="sgc-env-editor__empty">{t('environment.empty')}</p>
      ) : (
        <div className="sgc-env-editor__table" role="table" aria-label={t('environment.variables')}>
          <div className="sgc-env-editor__head" role="row">
            <span role="columnheader">{t('environment.keyLabel')}</span>
            <span role="columnheader">{t('environment.valueLabel')}</span>
            <span role="columnheader" aria-hidden="true" />
          </div>
          {rows.map((r, i) => {
            const problem = problems[i];
            const isRef = r.mode === 'ref';
            const shown = revealed[r.id];
            return (
              <div key={r.id} className={`sgc-env-editor__row ${problem ? 'sgc-env-editor__row--error' : ''}`} role="row">
                <div className="sgc-env-editor__cell sgc-env-editor__cell--key" role="cell">
                  <input
                    className="sgc-env-editor__input sgc-env-editor__input--mono"
                    value={r.key}
                    onChange={(e) => setRow(r.id, { key: e.target.value })}
                    placeholder="DATABASE_URL"
                    aria-label={t('environment.keyLabel')}
                    aria-invalid={problem && problem !== 'invalid_ref' ? 'true' : undefined}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <button type="button" className="sgc-env-editor__icon" onClick={() => copyKey(r.key)} title={t('environment.copyKey')} aria-label={t('environment.copyKey')}>
                    <CopyIcon width={16} height={16} aria-hidden="true" />
                  </button>
                </div>
                <div className="sgc-env-editor__cell sgc-env-editor__cell--value" role="cell">
                  <button
                    type="button"
                    className={`sgc-env-editor__mode ${isRef ? 'sgc-env-editor__mode--ref' : ''}`}
                    onClick={() => setRow(r.id, { mode: isRef ? 'value' : 'ref' })}
                    title={isRef ? t('environment.useValue') : t('environment.useReference')}
                    aria-label={isRef ? t('environment.useValue') : t('environment.useReference')}
                    aria-pressed={isRef}
                  >
                    <LinkIcon width={16} height={16} aria-hidden="true" />
                  </button>
                  {isRef ? (
                    <input
                      className="sgc-env-editor__input sgc-env-editor__input--mono"
                      value={r.ref}
                      onChange={(e) => setRow(r.id, { ref: e.target.value })}
                      placeholder="sgc://vault/item/field"
                      aria-label={t('environment.refLabel')}
                      aria-invalid={problem === 'invalid_ref' ? 'true' : undefined}
                      autoComplete="off"
                      spellCheck={false}
                    />
                  ) : (
                    <>
                      <input
                        className="sgc-env-editor__input sgc-env-editor__input--mono"
                        type={shown ? 'text' : 'password'}
                        value={r.value}
                        onChange={(e) => setRow(r.id, { value: e.target.value })}
                        aria-label={t('environment.valueLabel')}
                        autoComplete="new-password"
                        spellCheck={false}
                      />
                      <button type="button" className="sgc-env-editor__icon" onClick={() => setRevealed((p) => ({ ...p, [r.id]: !p[r.id] }))} title={t('dashboard.reveal')} aria-label={t('dashboard.reveal')} aria-pressed={Boolean(shown)}>
                        {shown ? <EyeOffIcon width={16} height={16} aria-hidden="true" /> : <EyeIcon width={16} height={16} aria-hidden="true" />}
                      </button>
                    </>
                  )}
                </div>
                <button type="button" className="sgc-env-editor__icon sgc-env-editor__icon--danger" onClick={() => removeRow(r.id)} title={t('environment.removeVariable')} aria-label={t('environment.removeVariable')}>
                  <TrashIcon width={16} height={16} aria-hidden="true" />
                </button>
                {problem && <p className="sgc-env-editor__error" role="alert">{t(`environment.errors.${problem}`)}</p>}
              </div>
            );
          })}
        </div>
      )}

      <PillButton variant="secondary" size="sm" icon={PlusIcon} onClick={() => update({ rows: [...rows, newRow()] })}>{t('environment.addVariable')}</PillButton>
      <p className="sgc-env-editor__hint">{t('environment.referenceHelp')}</p>

      <div className="sgc-env-editor__extends">
        <label htmlFor="env-extends">{t('environment.extendsLabel')}</label>
        <select id="env-extends" value={base || ''} onChange={(e) => update({ base: e.target.value })}>
          <option value="">{t('environment.extendsNone')}</option>
          {otherEnvironments.filter((n) => n.toLowerCase() !== String(selfName || '').trim().toLowerCase()).map((n) => <option key={n} value={n}>{n}</option>)}
          {base && !otherEnvironments.includes(base) && <option value={base}>{base}</option>}
        </select>
        <p className="sgc-env-editor__hint">{t('environment.extendsHelp')}</p>
      </div>

      <Modal open={importOpen} onClose={closeImport} title={t('environment.importTitle')}>
        <div className="sgc-env-editor__import">
          <label htmlFor="env-paste">{t('environment.importPaste')}</label>
          <textarea
            id="env-paste"
            value={pasted}
            onChange={(e) => { setPasted(e.target.value); setFileName(''); }}
            rows={8}
            spellCheck={false}
            autoComplete="off"
            placeholder={'DATABASE_URL=postgres://...\nLOG_LEVEL=info'}
          />
          <div className="sgc-env-editor__import-file">
            <input ref={fileInput} type="file" accept=".env,text/plain,.txt,*/*" onChange={onFile} hidden />
            <PillButton variant="secondary" size="sm" icon={UploadIcon} onClick={() => fileInput.current?.click()}>{t('environment.importFile')}</PillButton>
            {fileName && <span className="sgc-env-editor__file-name">{fileName}</span>}
          </div>
          <Toggle checked={replace} onChange={setReplace} label={t('environment.importReplace')} />
          <Toggle checked={remind} onChange={setRemind} label={t('environment.importRemind')} />
          <p className="sgc-env-editor__hint">{t('environment.importRemindHelp')}</p>
          {pasted.trim() && (
            <p className="sgc-env-editor__summary" role="status">
              {t('environment.importPreview', { count: preview.entries.length })}
              {preview.errors.length > 0 && ` ${t('environment.importLinesSkipped', { lines: preview.errors.map((e) => e.line).join(', ') })}`}
            </p>
          )}
          <div className="sgc-env-editor__import-actions">
            <PillButton variant="secondary" onClick={closeImport}>{t('common.cancel')}</PillButton>
            <PillButton disabled={preview.entries.length === 0} onClick={doImport}>{t('environment.importAction')}</PillButton>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default EnvironmentEditor;
